import { useDisclosure } from "@mantine/hooks"
import Client from "../../models/client"
import React, { useEffect, useMemo, useState } from "react"
import { applyColumnFilter, ColumnFilter, DEFAULT_FILTER_BY_TYPE, Filter, isFilterActive } from "../components/ColumnFilter"
import useSWR from "swr"
import { bulkDeleteItems, deleteItem, FETCH_KEYS, fetcherWithAuth, postItem } from "../api"
import { notifications } from "@mantine/notifications"
import { ActionIcon, Alert, alpha, Anchor, Box, Button, Group, Modal, Text, ThemeIcon, Title, Tooltip } from "@mantine/core"
import { CopyIcon, TrashIcon, WrenchIcon } from "@phosphor-icons/react"
import { MyDropzone } from "../components/dropzoneField"
import { DataTable } from "mantine-datatable"
import UploadForm from "../components/UploadForm"
import { TablePagination } from "../components/TablePagination"
import { confirmBulkDelete, confirmDelete } from "../utils/confirm"

interface Item {
  id: number
  name: string
  path: string
}

interface ItemsResponse {
  items: Item[]
}

interface Props {
  client: Client
}

// 一括操作バー
const bulkBannerStyles = {
  root: { background: alpha('var(--mantine-color-invy-6)', 0.06), border: '1px solid var(--mantine-color-invy-6)' },
}

const ItemsContainer = ({ client }: Props) => {
  const [uploadOpened, uploaderHandlers] = useDisclosure(false)
  const [previewOpened, previewHandlers] = useDisclosure(false)
  const [previewImage, setPreviewImage] = useState<string>('')
  const [droppedFiles, setDroppedFiles] = useState<File[]>([])
  const [selectedItems, setSelectedItems] = useState<Item[]>([])
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [filters, setFilters] = useState<Record<string, Filter>>({})

  const { data, isLoading, mutate } = useSWR<ItemsResponse>(
    FETCH_KEYS.items(client.id),
    fetcherWithAuth,
    { revalidateOnFocus: false },
  )

  const items = useMemo<Item[]>(() => data?.items ?? [], [data])

  const filteredRows = useMemo(() => {
    return items.filter((row) => {
      for (const field of ['name', 'path'] as const) {
        const f = filters[field]
        if (f && isFilterActive(f)) {
          if (!applyColumnFilter(row[field], f, 'string')) return false
        }
      }
      return true
    })
  }, [items, filters])

  const totalCount = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))

  const pagedRows = useMemo(() => {
    const from = (page - 1) * perPage
    return filteredRows.slice(from, from + perPage)
  }, [filteredRows, page, perPage])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    if (page > totalPages) handlePageChange(totalPages)
  }, [page, totalPages])

  useEffect(() => {
    setPage(1)
  }, [filters])

  const handleDrop = (files: File[]) => {
    setDroppedFiles(files)
    uploaderHandlers.open()
  }

  const handleUploaderClose = () => {
    setDroppedFiles([])
    uploaderHandlers.close()
  }

  const handleUpload = async (files: File[], directory: string) => {
    for (const file of files) {
      await postItem(client.id, file, directory)
    }
    handlePageChange(1)
    await mutate()
  }

  const handleDelete = async (item: Item) => {
    if (!confirmDelete('ファイル')) return
    try {
      await deleteItem(client.id, item.id)
      await mutate()
      setSelectedItems((prev) => prev.filter((i) => i.id !== item.id))
      notifications.show({ message: 'ファイルを削除しました', color: 'teal' })
    } catch (e) {
      console.error('ファイルの削除に失敗しました', e)
      notifications.show({ message: 'ファイルの削除に失敗しました', color: 'red' })
    }
  }

  const handleBulkDelete = async () => {
    if (!confirmBulkDelete(selectedItems.length, 'ファイル')) return
    setBulkDeleting(true)

    try {
      await bulkDeleteItems(client.id, selectedItems.map((i) => i.id))
      await mutate()
      setSelectedItems([])
      notifications.show({message: '選択したファイルを削除しました', color: 'teal'})
    } catch (e) {
      console.error('ファイルの一括削除に失敗しました', e)
      notifications.show({message: '一括削除に失敗しました', color: 'red'})
    } finally {
      setBulkDeleting(false)
    }
  }

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url)
    notifications.show({ message: 'URLをコピーしました', color: 'teal' })
  }

  const handleClickThumb = (path: string) => {
    setPreviewImage(path)
    previewHandlers.open()
  }

  const columns = useMemo(
    () => [
      {
        accessor: 'name',
        title: 'ファイル名',
        filter: (
          <ColumnFilter
            filter={filters.name ?? DEFAULT_FILTER_BY_TYPE.string}
            onChange={(f: Filter) => setFilters((prev) => ({ ...prev, name: f }))}
            dataType="string"
          />
        ),
        filtering: isFilterActive(filters.name),
      },
      {
        accessor: 'path',
        title: 'URL',
        width: "70%",
        filter: (
          <ColumnFilter
            filter={filters.path ?? DEFAULT_FILTER_BY_TYPE.string}
            onChange={(f: Filter) => setFilters((prev) => ({ ...prev, path: f }))}
            dataType="string"
          />
        ),
        filtering: isFilterActive(filters.path),
        render: (r: Item) => (
          <Group gap={4} wrap="nowrap">
            <Anchor href={r.path} target="_blank" rel="noopener noreferrer" fz="sm" style={{ wordBreak: "break-all" }}>
              {r.path}
            </Anchor>
            <Tooltip label="URLをコピー">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => handleCopy(r.path)}
              >
                <CopyIcon size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
      },
      {
        accessor: 'thumb',
        title: 'プレビュー',
        width: 120,
        textAlign: 'center' as const,
        render: (r: Item) => (
          <img
            src={r.path}
            onClick={() => handleClickThumb(r.path)}
            style={{
              maxHeight: 100,
              maxWidth: '100%',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        ),
      },
      {
        accessor: 'delete',
        title: '削除',
        width: 60,
        textAlign: 'center' as const,
        render: (r: Item) => (
          <Tooltip label="削除">
            <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(r)}>
              <TrashIcon size={16} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    [filters],
  )

  // 行数が少ないときにテーブル下部の余白が出ないよう、内部スクロールが必要な件数のときだけ
  // 高さを固定する。0件時は「データがありません」の表示領域を確保する。
  // （maxHeight は内部の ScrollArea が flex アイテムで縮まないため機能しない）
  const tableHeight = pagedRows.length === 0 ? 400 : pagedRows.length >= 15 ? 600 : undefined

  return (
    <Box>
      <Group gap={12} mb={40}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <WrenchIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">ファイルアップ</Title>
      </Group>

      <MyDropzone onDrop={handleDrop} />

      {selectedItems.length > 0 && (
        <Alert mt={40} styles={bulkBannerStyles}>
          <Group justify="space-between" wrap="nowrap">
            <Text>{selectedItems.length}件を選択中</Text>
            <Button
              variant="filled"
              color="invy"
              radius="xl"
              size="sm"
              onClick={handleBulkDelete}
              loading={bulkDeleting}
            >
              一括削除
            </Button>
          </Group>
        </Alert>
      )}
      <Box
        mt={40}
        style={{
          border: '1px solid #0000001A',
          borderRadius: 8,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <DataTable
          withColumnBorders
          fetching={isLoading}
          records={pagedRows}
          selectedRecords={selectedItems}
          onSelectedRecordsChange={setSelectedItems}
          idAccessor="id"
          noRecordsText="ファイルが登録されていません"
          height={tableHeight}
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#F8F9FA' },
          }}
          columns={columns}
        />

        <TablePagination
          page={page}
          onChangePage={handlePageChange}
          perPage={perPage}
          onChangePerPage={(v) => { setPerPage(v); handlePageChange(1) }}
          totalPages={totalPages}
          isEmpty={pagedRows.length === 0}
        />
      </Box>

      <UploadForm
        opened={uploadOpened}
        onClose={handleUploaderClose}
        urlName={client.url_name}
        files={droppedFiles}
        onUpload={handleUpload}
      />

      <Modal
        opened={previewOpened}
        onClose={previewHandlers.close}
        withCloseButton
        centered
        size="lg"
        padding="sm"
      >
        <img
          src={previewImage}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Modal>
    </Box>
  )
}

export default ItemsContainer
